import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChefHat, Facebook, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { trackWhatsAppClick } from '../lib/analytics';
import { DEFAULT_HOME_SECTIONS } from '../lib/homeDefaults';

export interface SiteFooterSection {
  logoUrl?: string;
  address?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  facebookUrl?: string;
  facebookIcon?: string;
}

const defaultFooterSection = DEFAULT_HOME_SECTIONS.find(
  (s) => s.type === 'footer'
) as SiteFooterSection;

// 全站統一頁腳：CMS 資料每個 session 只抓一次，各頁共用
let cachedSection: SiteFooterSection | null = null;
let cachedPromise: Promise<SiteFooterSection> | null = null;

function loadFooterSection(): Promise<SiteFooterSection> {
  if (cachedSection) return Promise.resolve(cachedSection);
  if (!cachedPromise) {
    cachedPromise = (async () => {
      let result = defaultFooterSection;
      try {
        const res = await apiFetch('/api/public/cms/pages/home');
        if (res.ok) {
          const data = await res.json();
          const found = Array.isArray(data?.sections)
            ? data.sections.find((s: SiteFooterSection & { type?: string }) => s?.type === 'footer')
            : null;
          if (found) result = found;
        }
      } catch {
        /* keep default */
      }
      cachedSection = result;
      return result;
    })();
  }
  return cachedPromise;
}

interface SiteFooterProps {
  /** 頁面本身已載入 CMS home sections 時可直接傳入，免再等 fetch */
  section?: SiteFooterSection | null;
}

export default function SiteFooter({ section }: SiteFooterProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fetched, setFetched] = useState<SiteFooterSection | null>(cachedSection);

  useEffect(() => {
    if (section || cachedSection) return;
    let alive = true;
    loadFooterSection().then((s) => {
      if (alive) setFetched(s);
    });
    return () => {
      alive = false;
    };
  }, [section]);

  const s = section || fetched || defaultFooterSection;
  const whatsappDigits = (s.whatsapp || '57961378').replace(/\D/g, '');

  return (
    <footer id="contact" className="bg-[#EB6B00] text-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          <div className="lg:col-span-1">
            {s.logoUrl ? (
              <img
                src={s.logoUrl}
                alt={t('common:brand.suffix')}
                className="mb-4 h-auto max-h-20 w-auto object-contain"
              />
            ) : (
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-white text-stone-400">
                <ChefHat size={32} />
              </div>
            )}
            <p className="text-sm text-white/90 leading-relaxed">{t('common:footer.aboutText')}</p>
          </div>
          <div>
            <h3 className="font-bold text-white mb-4">{t('common:footer.contactTitle')}</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-white/90 flex-shrink-0" />
                <span>{s.address || t('common:footer.addressFallback')}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-white/90 flex-shrink-0" />
                <span>{s.phone || '35946130'}</span>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle size={16} className="text-white/90 flex-shrink-0" />
                <a
                  href={`https://wa.me/852${whatsappDigits}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick(undefined, 'footer')}
                  className="hover:text-white transition-colors"
                >
                  WhatsApp {s.whatsapp || '57961378'}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-white/90 flex-shrink-0" />
                <a
                  href={`mailto:${s.email || 'support@deliciousexpress.com.hk'}`}
                  className="hover:text-white transition-colors"
                >
                  {s.email || 'support@deliciousexpress.com.hk'}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-white mb-4">{t('common:footer.followTitle')}</h3>
            <a
              href={s.facebookUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166fe5] transition-colors"
            >
              {s.facebookIcon ? (
                <img src={s.facebookIcon} alt="Facebook" className="w-5 h-5" />
              ) : (
                <Facebook size={18} />
              )}
              <span>Facebook</span>
            </a>
          </div>
          <div>
            <h3 className="font-bold text-white mb-4">{t('common:footer.linksTitle')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <button
                  onClick={() => navigate('/')}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {t('common:footer.linkShop')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/about')}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {t('common:footer.linkAbout')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {t('common:footer.backToTop')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/terms-of-service')}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {t('common:footer.terms')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/privacy-policy')}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {t('common:footer.privacy')}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-white/30 text-center text-xs text-white/70">
          Powered by{' '}
          <a
            href="https://jkdcoding.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:underline"
            style={{ color: '#CCFF00' }}
          >
            JKDCoding
          </a>
        </div>
      </div>
    </footer>
  );
}
